import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import india from "@svg-maps/india";

// Real-geography pledge wall: the accurate India silhouette (from
// @svg-maps/india, viewBox 0 0 612 696) is used as a clipPath over a grid of
// mini pledge cards. Cards are tinted tricolor by vertical thirds and an
// Ashoka Chakra sits in the white band, matching the campaign concept art.
//
// With more pledges than tiles, tiles continuously rotate through the record
// set (a few tiles swap every tick with a crossfade), so every doctor gets
// screen time on a long-running kiosk display. Clicking a tile reports the
// pledge via onSelect for the record popup.

const VIEW_W = 612;
const VIEW_H = 696;
const TILE_W = 34;
const TILE_H = 40;
const GAP = 2;

const ROTATE_MS = 2500; // how often a batch of tiles swaps
const ROTATE_BATCH = 9; // tiles swapped per tick

const SAFFRON = "#FF9933";
const GREEN = "#138808";
const NAVY = "#06038D";

// Vertical thirds of the map determine the flag band.
function bandFor(cy) {
  if (cy < VIEW_H / 3) return "saffron";
  if (cy < (2 * VIEW_H) / 3) return "white";
  return "green";
}

const BAND_STYLE = {
  saffron: { bg: "#ffb75e", card: "#ff9933", text: "#7c2d12", photoRing: "#ffedd5" },
  white: { bg: "#f8fafc", card: "#ffffff", text: "#1e293b", photoRing: "#e2e8f0" },
  green: { bg: "#2ea043", card: "#138808", text: "#dcfce7", photoRing: "#bbf7d0" },
};

function initialsOf(name = "") {
  return name
    .replace(/^Dr\.?\s*/i, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "DR";
}

function firstNameOf(name = "", raw = false) {
  const clean = name.replace(/^Dr\.?\s*/i, "").trim();
  const first = (clean.split(" ")[0] || "").replace(/[A-Za-z]+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  if (!first) return "";
  return raw ? first : `Dr. ${first}`;
}

// Rendered BEHIND the doctor tiles as a large national watermark.
function Chakra({ cx, cy, r, opacity = 0.28 }) {
  const spokes = Array.from({ length: 24 }, (_, i) => (i * 360) / 24);
  return (
    <g opacity={opacity} pointerEvents="none">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={NAVY} strokeWidth="6" />
      <circle cx={cx} cy={cy} r={7} fill={NAVY} />
      {spokes.map((deg) => (
        <line
          key={deg}
          x1={cx}
          y1={cy}
          x2={cx + (r - 3) * Math.cos((deg * Math.PI) / 180)}
          y2={cy + (r - 3) * Math.sin((deg * Math.PI) / 180)}
          stroke={NAVY}
          strokeWidth="3"
        />
      ))}
    </g>
  );
}

function Tile({ x, y, pledge, band, onSelect, rawNames = false }) {
  const style = BAND_STYLE[band];
  const w = TILE_W - GAP;
  const h = TILE_H - GAP;
  if (!pledge) {
    return <rect x={x} y={y} width={w} height={h} rx={3} fill={style.card} opacity={band === "white" ? 0.9 : 0.55} />;
  }
  const photoH = h - 11;
  return (
    <motion.g
      key={pledge.id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      onClick={() => onSelect?.(pledge)}
      style={{ cursor: "pointer" }}
      data-testid="wall-map-tile"
    >
      <title>{[pledge.name, pledge.specialty, pledge.city].filter(Boolean).join(" — ")}</title>
      <rect x={x} y={y} width={w} height={h} rx={3} fill={style.card} />
      {pledge.photo ? (
        <image
          href={pledge.photo}
          x={x + 2}
          y={y + 2}
          width={w - 4}
          height={photoH - 2}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <>
          <rect x={x + 2} y={y + 2} width={w - 4} height={photoH - 2} rx={2} fill={style.photoRing} />
          <text
            x={x + w / 2}
            y={y + 2 + (photoH - 2) / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="11"
            fontWeight="800"
            fill={NAVY}
            fontFamily="Outfit, sans-serif"
          >
            {initialsOf(pledge.name)}
          </text>
        </>
      )}
      <text
        x={x + w / 2}
        y={y + h - 4}
        textAnchor="middle"
        fontSize="6"
        fontWeight="700"
        fill={style.text}
        fontFamily="Outfit, sans-serif"
      >
        {firstNameOf(pledge.name, rawNames).slice(0, 12)}
      </text>
    </motion.g>
  );
}

export function IndiaMosaicMap({ pledges = [], onSelect, rawNames = false }) {
  const tiles = useMemo(() => {
    const out = [];
    for (let y = 0; y + TILE_H <= VIEW_H + TILE_H; y += TILE_H) {
      for (let x = 0; x + TILE_W <= VIEW_W + TILE_W; x += TILE_W) {
        out.push({ x, y, band: bandFor(y + TILE_H / 2), key: `${x}-${y}` });
      }
    }
    return out;
  }, []);

  // assignments[tileIndex] = index into pledges. Rotated in small random
  // batches so the wall keeps moving without everything changing at once.
  const [assignments, setAssignments] = useState([]);
  const newestIdRef = useRef(null);
  const seededRef = useRef(false);

  // (Re)spread pledges across tiles once real data is available; before that
  // point every assignment is 0 and must not be kept.
  useEffect(() => {
    if (seededRef.current && pledges.length) return;
    seededRef.current = pledges.length > 0;
    setAssignments(tiles.map((_, i) => (pledges.length ? i % pledges.length : 0)));
  }, [tiles, pledges.length]);

  useEffect(() => {
    if (pledges.length <= 1) return;
    const t = setInterval(() => {
      setAssignments((prev) =>
        prev.map((v, i) =>
          Math.random() < ROTATE_BATCH / prev.length
            ? Math.floor(Math.random() * pledges.length)
            : v
        )
      );
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [pledges.length]);

  // When a brand-new pledge arrives, drop it onto a tile near the middle of
  // the map right away instead of waiting for random rotation to reach it.
  useEffect(() => {
    const newest = pledges[0]?.id;
    if (!newest || newest === newestIdRef.current) return;
    const hadPrevious = newestIdRef.current !== null;
    newestIdRef.current = newest;
    if (!hadPrevious) return; // initial load, no arrival animation needed
    setAssignments((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const centerTile = tiles.findIndex(
        (t) => Math.abs(t.x - 250) < TILE_W * 2 && Math.abs(t.y - VIEW_H / 2 + 80) < TILE_H * 2
      );
      next[centerTile >= 0 ? centerTile : Math.floor(prev.length / 2)] = 0;
      return next;
    });
  }, [pledges, tiles]);

  return (
    <div data-testid="india-mosaic-map" className="w-full h-full flex justify-center">
      <svg
        viewBox={`-8 -8 ${VIEW_W + 16} ${VIEW_H + 16}`}
        className="w-full h-full"
        role="img"
        aria-label="Live pledge wall — doctors' pledges forming the map of India"
      >
        <defs>
          <clipPath id="india-silhouette">
            {india.locations.map((loc) => (
              <path key={loc.id} d={loc.path} />
            ))}
          </clipPath>
        </defs>

        {/* soft drop shadow behind the map */}
        <g clipPath="url(#india-silhouette)" transform="translate(3,4)" opacity="0.15">
          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="#0f172a" />
        </g>

        <g clipPath="url(#india-silhouette)">
          {/* flag-band backdrop so tile gaps read tricolor */}
          <rect x="0" y="0" width={VIEW_W} height={VIEW_H / 3} fill={SAFFRON} />
          <rect x="0" y={VIEW_H / 3} width={VIEW_W} height={VIEW_H / 3} fill="#f1f5f9" />
          <rect x="0" y={(2 * VIEW_H) / 3} width={VIEW_W} height={VIEW_H / 3} fill={GREEN} />
          <Chakra cx={250} cy={VIEW_H / 2} r={100} opacity={0.35} />
          {tiles.map((t, i) => (
            <Tile
              key={t.key}
              x={t.x + GAP / 2}
              y={t.y + GAP / 2}
              pledge={pledges.length ? pledges[(assignments[i] ?? i) % pledges.length] : null}
              band={t.band}
              onSelect={onSelect}
              rawNames={rawNames}
            />
          ))}
          {/* faint echo over the tiles so the chakra reads as a watermark */}
          <Chakra cx={250} cy={VIEW_H / 2} r={100} opacity={0.16} />
        </g>

        {/* real state borders on top, so the geography reads clearly */}
        <g fill="none" stroke="#0f172a" strokeOpacity="0.18" strokeWidth="0.8" pointerEvents="none">
          {india.locations.map((loc) => (
            <path key={loc.id} d={loc.path} />
          ))}
        </g>
      </svg>
    </div>
  );
}
